import { AnalyticsBrowser } from '@segment/analytics-next';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BusinessSegment, BusinessType } from '../api-client';
import type { FormContextData } from '../state';
import { getCountryCodeFromTLD } from '../helpers';

// For segment tracking, we set the country code based on the current domain name
const country = getCountryCodeFromTLD();

export enum SegmentEventType {
  BUSINESS_SEARCH = 'business_search',
  ADD_INFO = 'add_info',
  ADD_SERVICES = 'add_services',
  ADD_BUSINESS_TYPE = 'add_business_type',
  ABOUT_SIGN_UP = 'self_sign_up_click',
  ERROR_MESSAGE_SHOWN = 'error_message_shown',
  LOGIN_REDIRECT = 'login_redirect',
  CONTINUE_WITH_SIGNUP = 'continue_with_signup',
  CONTEXT_STEP_CLICK = 'context_step_click',
  TURNSTILE_FALLBACK_TRIGGERED = 'turnstile_fallback_triggered',
  VENUE_CREATED = 'signup.self_signup.venue.created',
}

// User classification the legacy TS backend attached to `self_sign_up_venue_created`
// before it was dropped in the SSU→Java migration (CPG-798). In the current flow an
// existing B2B account is redirected to login before reaching sign-up, so only NEW and
// EXISTING_B2C are emitted from here; EXISTING_B2B is kept for parity with the old values.
export enum UserType {
  NEW = 'new',
  EXISTING_B2C = 'existing_b2c',
  EXISTING_B2B = 'existing_b2b',
}

type BusinessSearchPayload = {
  step: 'AddInfo';
  action: 'search' | 'manual' | 'skip';
};

type AddInfoPayload = {
  step: 'AddInfo';
  mode: 'manual' | 'automatic';
};
type AddServicesPayload = {
  step: 'AddServices';
  services_selection: BusinessSegment[];
};
type AddBusinessTypePayload = {
  step: 'AddBusinessType';
  business_selection: BusinessType;
};
type AboutSignUpPayload = {
  step: 'AboutSignUp';
  marketing_opt_in: boolean;
};
type LoginRedirectPayload = {
  step: 'LoginRedirect';
};
type ContinueWithSignupPayload = {
  step: 'LoginRedirect';
};
type ContextStepPayload = {
  step: 'ContextStep';
};
type TurnstileFallbackTriggeredPayload = {
  // 'token_absent'  — widget did not fire before Sign Up was clicked
  //                   (sitekey mismatch, script load failure, or unsupported domain)
  // 'token_expired' — Connect rejected the token with a 401/403,
  //                   most likely because the job ran past the token TTL
  reason: 'token_absent' | 'token_expired';
};
export type ErrorMessageShownPayload = {
  step:
    | 'AboutSignUp'
    | 'AddInfo'
    | 'AddServices'
    | 'AddBusinessType'
    | 'VerificationCode'
    | 'unknown';
  errors: string[];
  business_name?: string;
  address?: string;
};

type BusinessSearchFunction = (
  event: SegmentEventType.BUSINESS_SEARCH,
  payload: BusinessSearchPayload,
) => void;
type AddInfoFunction = (
  event: SegmentEventType.ADD_INFO,
  payload: AddInfoPayload,
) => void;
type AddServicesFunction = (
  event: SegmentEventType.ADD_SERVICES,
  payload: AddServicesPayload,
) => void;
type AddBusinessTypeFunction = (
  event: SegmentEventType.ADD_BUSINESS_TYPE,
  payload: AddBusinessTypePayload,
) => void;
type AboutSignUpFunction = (
  event: SegmentEventType.ABOUT_SIGN_UP,
  payload: AboutSignUpPayload,
) => void;
type ErrorMessageShownFunction = (
  event: SegmentEventType.ERROR_MESSAGE_SHOWN,
  payload: ErrorMessageShownPayload,
) => void;
type LoginRedirectFunction = (
  event: SegmentEventType.LOGIN_REDIRECT,
  payload: LoginRedirectPayload,
) => void;
type ContinueWithSignupFunction = (
  event: SegmentEventType.CONTINUE_WITH_SIGNUP,
  payload: ContinueWithSignupPayload,
) => void;
type ContextStepFunction = (
  event: SegmentEventType.CONTEXT_STEP_CLICK,
  payload: ContextStepPayload,
) => void;
type TurnstileFallbackTriggeredFunction = (
  event: SegmentEventType.TURNSTILE_FALLBACK_TRIGGERED,
  payload: TurnstileFallbackTriggeredPayload,
) => void;

// The venue-created event needs a group association (the venue), so it doesn't
// fit the generic `trackEvent` shape — it has its own method.
type VenueCreatedParams = {
  venueId: number | null | undefined;
  userType: UserType;
  countryCode: string;
};

type IdentifyParams = Pick<
  FormContextData,
  'firstName' | 'lastName' | 'phoneNumber' | 'email'
> & {
  communityAccountId: number | null | undefined;
};

type SegmentContext = {
  trackEvent: BusinessSearchFunction &
    AddInfoFunction &
    AddServicesFunction &
    AddBusinessTypeFunction &
    AboutSignUpFunction &
    LoginRedirectFunction &
    ContinueWithSignupFunction &
    ContextStepFunction &
    TurnstileFallbackTriggeredFunction &
    ErrorMessageShownFunction;
  identify: (params: IdentifyParams) => void;
  trackVenueCreated: (params: VenueCreatedParams) => void;
};

const SegmentContext = createContext<SegmentContext>({
  trackEvent: () => {},
  identify: () => {},
  trackVenueCreated: () => {},
});

const segment = process.env.SEGMENT_WRITE_KEY
  ? AnalyticsBrowser.load({ writeKey: process.env.SEGMENT_WRITE_KEY })
  : null;

export function SegmentContextProvider(props: PropsWithChildren) {
  const trackEvent = useCallback<SegmentContext['trackEvent']>(
    (event: SegmentEventType, payload: Record<string, unknown>) => {
      if (!segment) {
        return;
      }

      segment.track(event, { ...payload, country });
    },
    [],
  );

  const identify = useCallback<SegmentContext['identify']>(
    (params: IdentifyParams) => {
      if (!segment) {
        return;
      }

      const {
        firstName,
        lastName,
        phoneNumber: phone,
        email,
        communityAccountId,
      } = params;
      const traits = { firstName, lastName, phone, email };
      // Segment's user id is shared with other Treatwell sites on this domain, so it
      // may belong to someone else. Only identify(id) replaces it (a userId option on
      // track() is overwritten by the stored one). Connect identifies with the same id.
      if (communityAccountId == null) {
        segment.identify(traits);
      } else {
        segment.identify(String(communityAccountId), traits);
      }
    },
    [],
  );

  const trackVenueCreated = useCallback<SegmentContext['trackVenueCreated']>(
    ({ venueId, userType, countryCode }) => {
      if (!segment) {
        return;
      }
      // venue_id is emitted as a number to match every other Mixpanel event
      // (the legacy string broke numeric filters like `venue_id >= N`).
      segment.track(
        SegmentEventType.VENUE_CREATED,
        {
          country: countryCode,
          venue_id: venueId ?? undefined,
          user_type: userType,
        },
        { context: { groupId: String(venueId ?? '') } },
      );
    },
    [],
  );

  useEffect(() => {
    if (!segment) {
      return;
    }

    segment.page(undefined, undefined, { country });
  }, []);

  const value = useMemo(
    () => ({ trackEvent, identify, trackVenueCreated }),
    [trackEvent, identify, trackVenueCreated],
  );

  return (
    <SegmentContext.Provider value={value}>
      {props.children}
    </SegmentContext.Provider>
  );
}

export function useSegmentContext() {
  return useContext(SegmentContext);
}
